import {DecimalPipe, NgStyle} from "@angular/common"
import {Component, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {TextureType} from "@generated"
import {BaseImageTextureSetWidgetComponent} from "@material-editor/components/widgets/base-image-texture-set-widget/base-image-texture-set-widget.component"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {TextureFromSet, textureTypeSchema, toLegacyTextureType} from "@cm/material-nodes"
import * as changeCase from "change-case"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-from-set-widget",
    templateUrl: "./texture-from-set-widget.component.html",
    styleUrls: ["./texture-from-set-widget.component.scss"],
    imports: [
        NumericInputComponent,
        InputContainerComponent,
        MatMenuModule,
        DecimalPipe,
        MatFormFieldModule,
        MatSelectModule,
        NgStyle,
        DataObjectThumbnailComponent,
    ],
})
export class TextureFromSetWidgetComponent extends BaseImageTextureSetWidgetComponent {
    readonly $textureTypeOptions = signal<TextureType[] | undefined>(undefined)
    readonly $textureType = signal<TextureType | undefined>(undefined)

    get node() {
        return this.$material() as TextureFromSet
    }

    async changeTextureSetRevision(revisionId: string | undefined) {
        await this.loadTextureSetRevision(revisionId)
        this.$textureTypeOptions.set(this.$textureSetRevision()?.mapAssignments.map((mapAssignment) => mapAssignment.textureType) ?? [])
        this.changeTextureType(undefined, false)
        this.setupTextureInfo()
        this.setTextureSetRevisionVersion(revisionId)
        this.updateNodeParameters(true)
    }

    async changeTextureSet(textureSetLegacyId: number | undefined) {
        await this.loadTextureSet(textureSetLegacyId)
        await this.changeTextureSetRevision(this.$textureSetRevisionInfos()?.[0]?.id)
        this.previewSceneService.$refresh.next()
    }

    changeTextureType(textureType?: TextureType, updateNode: boolean = true) {
        this.$textureType.set(textureType ?? this.$textureTypeOptions()?.[0])
        this.setThumbnail()
        if (updateNode) this.updateNodeParameters(true)
    }

    async fromNodeParameters() {
        const textureSetRevisionId = this.node.parameters.settings.textureSetRevisionId
        if (!textureSetRevisionId) {
            this.resetParameters()
            this.updateNodeParameters(true)
            return
        }

        await this.loadTextureSetRevision(textureSetRevisionId)
        await this.loadTextureSet(this.$textureSetRevision()?.textureSet?.legacyId)
        this.setTextureSetRevisionVersion(textureSetRevisionId)

        this.$textureTypeOptions.set(this.$textureSetRevision()?.mapAssignments.map((mapAssignment) => mapAssignment.textureType) ?? [])
        if (!this.node.parameters.settings.mapAssignmentTextureType) throw new Error(`Texture type not set in node parameters: ${this.node}`)
        const textureType = textureTypeSchema.parse(changeCase.pascalCase(this.node.parameters.settings.mapAssignmentTextureType))
        if (!textureType) throw new Error(`Failed to get texture type from parameters: ${this.node}`)
        this.$textureType.set(textureType as TextureType)

        this.setThumbnail()
    }

    updateNodeParameters(applyDefaults: boolean) {
        this.node.updateSetting("textureSetRevisionId", this.$textureSetRevision()?.id)
        const textureType = this.$textureType()
        this.node.updateSetting("mapAssignmentTextureType", textureType ? toLegacyTextureType(textureType) : undefined)

        if (applyDefaults) {
            this.node.applyDefaults()
        }
        this.triggerGraphUpdate()
    }

    resetNodeParameters() {
        this.node.updateSetting("textureSetRevisionId", undefined)
        this.node.updateSetting("mapAssignmentTextureType", undefined)
    }

    resetParameters() {
        this.$textureSetRevision.set(undefined)
        this.$textureSetLegacyId.set(undefined)
        this.$textureSetRevisionInfos.set(undefined)
        this.$textureSetRevisionVersion.set(undefined)
        this.$thumbnailId.set(undefined)
        this.$textureType.set(undefined)
        this.$textureTypeOptions.set(undefined)
    }

    setThumbnail() {
        this.$thumbnailId.set(
            this.$textureType()
                ? this.$textureSetRevision()?.mapAssignments.filter((mapAssignment) => mapAssignment.textureType === this.$textureType())?.[0].dataObject.id
                : undefined,
        )
    }

    protected readonly TextureType = TextureType
}
